import React from 'react';
import { TabOwnProps, TabProps, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { NavLink, useLocation } from 'react-router-dom';
import Tooltip from '@mui/material/Tooltip';
import { themeColors } from '@styles/theme';
import { useTheme } from '@context/themeContext';

export type LeftTabs = {
    path: string;
    label?: string;
    url: string;
} & TabOwnProps;

type Props = {
    tabs: TabProps<any, LeftTabs>[];
};

const IconTabs = ({ tabs }: Props) => {
    const location = useLocation();
    const { theme } = useTheme();

    const inactiveTextColor = theme === 'light' ? themeColors.grey2 : themeColors.grey6;
    const inactiveBackground = theme === 'light' ? themeColors.white : themeColors.grey5;
    const inactiveBorderColor = theme === 'light' ? themeColors.grey : themeColors.grey4;
    const activeTextColor = theme === 'light' ? themeColors.navyBlue : themeColors.white;
    const activeBackground = theme === 'light' ? themeColors.grey1 : themeColors.grey4;
    const activeBorderColor = theme === 'light' ? themeColors.blueSky : themeColors.grey3;
    const hoverBackground = theme === 'light' ? themeColors.lightGrey : themeColors.grey4;
    const focusOutline = theme === 'light' ? themeColors.blueSky : themeColors.purple2;

    return (
        <ToggleButtonGroup exclusive sx={{ alignItems: 'stretch' }}>
            {tabs?.map(({ label, icon, ...props }, ind) => (
                <Tooltip
                    key={ind}
                    title={icon ? (label ?? '') : ''}
                    placement="bottom"
                    disableInteractive
                >
                    <span data-testid={props.testId}>
                        <NavLink
                            to={`${props.url}${location.search}`}
                            end={props.path === ''}
                            className={({ isActive, isPending }) =>
                                isPending ? 'pending' : isActive ? 'active' : ''
                            }
                        >
                            {({ isActive, isPending }) => (
                                <ToggleButton
                                    value={ind}
                                    selected={isActive}
                                    sx={{
                                        height: 36,
                                        minHeight: 36,
                                        boxSizing: 'border-box',
                                        color: inactiveTextColor,
                                        backgroundColor: inactiveBackground,
                                        borderColor: inactiveBorderColor,
                                        borderWidth: 1,
                                        borderStyle: 'solid',
                                        opacity: 0.92,
                                        transition:
                                            'background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease',
                                        '&.Mui-selected': {
                                            color: activeTextColor,
                                            backgroundColor: activeBackground,
                                            borderColor: activeBorderColor,
                                            opacity: 1,
                                            minHeight: 36,
                                        },
                                        '&.Mui-selected:hover': {
                                            backgroundColor: activeBackground,
                                        },
                                        '&:hover': {
                                            backgroundColor: hoverBackground,
                                            opacity: 1,
                                        },
                                        '&:focus-visible': {
                                            outline: `2px solid ${focusOutline}`,
                                            outlineOffset: 2,
                                        },
                                    }}
                                >
                                    {icon ?? label}
                                </ToggleButton>
                            )}
                        </NavLink>
                    </span>
                </Tooltip>
            ))}
        </ToggleButtonGroup>
    );
};

export default IconTabs;
